import { api } from '../api';

export type MetodoOtp = 'email' | 'sms';

export interface MetodoOtpInfo {
  tipo: MetodoOtp;
  mask: string;
}

export interface BuscarLegajoResultado {
  found: boolean;
  legajoNumero?: string;
  titularNombre?: string;
  primerNombre?: string;
  expiraAt?: string;
  requiereOtp?: boolean;
  metodos?: MetodoOtpInfo[];
  motivo?:
    | 'documento-no-existe'
    | 'documento-no-titular'
    | 'padron-no-cerrado'
    | 'legajo-vencido'
    | 'legajo-ya-validado';
}

export async function buscarLegajo(documento: string): Promise<BuscarLegajoResultado> {
  const { data } = await api.get<BuscarLegajoResultado>('/portal/buscar-legajo', {
    params: { documento },
  });
  return data;
}

export interface OtpSolicitarResp {
  ok: boolean;
  expiraAt: string;
  canal: string;
}

export async function solicitarOtp(
  documento: string,
  metodo?: MetodoOtp,
): Promise<OtpSolicitarResp> {
  const { data } = await api.post<OtpSolicitarResp>('/portal/otp/solicitar', {
    documento,
    metodo,
  });
  return data;
}

/**
 * El titular no tiene acceso a NINGUNO de los contactos cargados (pasa cuando
 * el padrón trajo el mail/celular de otra persona). Deja el contacto propuesto
 * como observación del legajo para que lo apruebe Medicus o la empresa. No
 * manda el OTP al contacto nuevo: la aprobación humana es la que evita que
 * cualquiera con un DNI se apropie de un legajo.
 */
export async function reportarContactoAlternativo(body: {
  documento: string;
  tipo: 'email' | 'celular';
  valor: string;
}): Promise<{ ok: boolean; legajoNumero: string }> {
  const { data } = await api.post<{ ok: boolean; legajoNumero: string }>(
    '/portal/contacto-alternativo',
    body,
  );
  return data;
}

export interface OtpValidarResp {
  ok: boolean;
  activacionToken: string;
  legajoNumero: string;
}

export async function validarOtp(
  documento: string,
  code: string,
): Promise<OtpValidarResp> {
  const { data } = await api.post<OtpValidarResp>('/portal/otp/validar', {
    documento,
    code,
  });
  return data;
}

// ---------- Onboarding ----------

export interface IntegranteDto {
  id: string;
  legajo?: string;
  parentesco?: string;
  apellSolt?: string;
  nombres?: string;
  fechaNacimiento?: string;
  nacionalidad?: string;
  sexo?: string;
  estadoCivil?: string;
  tipoDoc?: string;
  documento?: string;
  cuil?: string;
  domicilio?: string;
  numero?: string;
  piso?: string;
  depto?: string;
  cp?: string;
  areaCelular?: string;
  numeroCelular?: string;
  email?: string;
}

export interface ObservacionTitular {
  campo: string;
  asociadoId: string;
  /**
   * Valor correcto propuesto por el titular. Para enums (sexo, estadoCivil,
   * parentesco, nacionalidad, tipoDoc) es el código del catálogo; para el
   * resto (documento, fechaNacimiento, nombres, etc.) es el texto/fecha que
   * el titular escribió. Opcional por compat con observaciones viejas.
   */
  valorPropuesto?: string;
  /**
   * Texto libre opcional. Hoy sólo se usa en `sexo` para que el titular pueda
   * aclarar identidades no contempladas en M/F.
   */
  mensaje?: string;
  reportadoAt: string;
}

export interface LegajoOnboardingOk {
  needsOtp: false;
  legajo: {
    id: string;
    numero: string;
    estado: string;
    activacionExpiraAt: string;
    planSuperadorElegido?: boolean | null;
    planSuperadorDecididoAt?: string;
    /**
     * La empresa del legajo ofrece un plan opcional además del base. Si es
     * `false`, el wizard omite los pasos "Plan" y "Pago" — no hay nada que
     * elegir ni que cobrar. Ausente en respuestas viejas → se asume `true`
     * para no romper el flujo de MS mientras se despliega.
     */
    ofrecePlanOpcional?: boolean;
    observaciones?: ObservacionTitular[];
  };
  titular: IntegranteDto;
  familiares: IntegranteDto[];
}

export interface LegajoOnboardingNeedsOtp {
  needsOtp: true;
  documento: string;
  primerNombre: string;
  metodos: MetodoOtpInfo[];
}

/**
 * Cuando el legajo está bloqueado por la validación de admin Medicus el
 * server devuelve este envelope en vez de los datos del wizard. El portal
 * lo usa para mostrar un mensaje claro al titular.
 */
export interface LegajoOnboardingBlockedByMedicus {
  blockedByMedicus: true;
  // `bloqueado`: la persona (titular) está frenada a nivel individuo.
  motivo: 'pendiente_validacion' | 'rechazado' | 'bloqueado';
  mensaje?: string;
}

export type LegajoOnboarding =
  | LegajoOnboardingOk
  | LegajoOnboardingNeedsOtp
  | LegajoOnboardingBlockedByMedicus;

export function isBlockedByMedicus(
  data: LegajoOnboarding,
): data is LegajoOnboardingBlockedByMedicus {
  return 'blockedByMedicus' in data && data.blockedByMedicus;
}

export function isNeedsOtp(data: LegajoOnboarding): data is LegajoOnboardingNeedsOtp {
  return 'needsOtp' in data && data.needsOtp === true;
}

export async function getLegajoByToken(token: string): Promise<LegajoOnboarding> {
  const { data } = await api.get<LegajoOnboarding>(`/portal/legajo/${token}`);
  return data;
}

export async function patchTitular(
  token: string,
  patch: Partial<IntegranteDto>,
): Promise<IntegranteDto> {
  const { data } = await api.put<IntegranteDto>(`/portal/legajo/${token}/titular`, patch);
  return data;
}

export interface EmailEnUsoResp {
  /** true si el email de contacto del titular ya lo usa otro socio dado de alta. */
  enUso: boolean;
  email: string;
  mensaje?: string;
}

/**
 * Chequea contra la base si el email de contacto actual del titular ya está
 * siendo usado por otro socio dado de alta. Lo usa el paso del cuestionario de
 * salud antes de avanzar al plan (cada reintento vuelve a pegarle al server).
 */
export async function chequearEmailEnUso(token: string): Promise<EmailEnUsoResp> {
  const { data } = await api.get<EmailEnUsoResp>(`/portal/legajo/${token}/email-en-uso`);
  return data;
}

export interface AddFamiliarDto {
  parentesco: '5' | '7' | '13';
  apellSolt: string;
  nombres: string;
  fechaNacimiento: string;
  documento: string;
  tipoDoc?: string;
  cuil?: string;
  nacionalidad?: string;
  sexo?: string;
  estadoCivil?: string;
  email?: string;
  areaCelular?: string;
  numeroCelular?: string;
}

export async function addFamiliar(token: string, dto: AddFamiliarDto): Promise<IntegranteDto> {
  const { data } = await api.post<IntegranteDto>(`/portal/legajo/${token}/familiar`, dto);
  return data;
}

export async function patchFamiliar(
  token: string,
  asociadoId: string,
  patch: Partial<IntegranteDto>,
): Promise<IntegranteDto> {
  const { data } = await api.put<IntegranteDto>(
    `/portal/legajo/${token}/familiar/${asociadoId}`,
    patch,
  );
  return data;
}

export async function deleteFamiliar(token: string, asociadoId: string): Promise<{ ok: boolean }> {
  const { data } = await api.delete<{ ok: boolean }>(
    `/portal/legajo/${token}/familiar/${asociadoId}`,
  );
  return data;
}

/**
 * Reporta un dato como incorrecto. Queda persistido en el legajo como
 * observación para que RRHH/Medicus lo vea desde el backoffice.
 */
export async function reportarDato(
  token: string,
  payload: {
    asociadoId: string;
    campo: string;
    /** Valor correcto propuesto (texto, fecha, o código de enum). Obligatorio. */
    valorPropuesto: string;
    /** Comentario opcional (sólo en `sexo`). */
    mensaje?: string;
  },
): Promise<{ ok: boolean; observacion: ObservacionTitular; total: number }> {
  const { data } = await api.post<{
    ok: boolean;
    observacion: ObservacionTitular;
    total: number;
  }>(`/portal/legajo/${token}/observaciones`, payload);
  return data;
}

// ---------- DDJJ ----------

export type DdjjFieldType =
  | 'bool'
  | 'bool+text'
  | 'bool+text2'
  | 'text'
  | 'date'
  | 'select'
  | 'file';

export interface DdjjPregunta {
  codigo: string;
  seccion: number;
  seccionTitulo: string;
  pregunta: string;
  tipo: DdjjFieldType;
  opciones?: string[];
  /** Label del campo de texto (bool+text) o del primer campo (bool+text2). */
  textLabel?: string;
  /** Label del segundo campo de texto — solo para bool+text2. */
  textLabel2?: string;
  /** Para tipo 'file': mime types aceptados (ej. ['application/pdf']). */
  mimeAceptados?: string[];
  /** Para tipo 'file': tamaño máximo en MB. */
  tamMaxMB?: number;
  soloTitular?: boolean;
  /** Si true, la respuesta es opcional (no exigida para "completar" el cuestionario). */
  opcional?: boolean;
  /** Si true (solo cuando soloTitular=true), el valor se aplica también a los familiares al exportar. */
  heredaAFamiliares?: boolean;
  dependeDe?: { codigo: string; valor: string };
}

export interface RespuestaArchivo {
  fileId: string;
  name: string;
  size: number;
  mime: string;
  uploadedAt: string;
}

export interface DdjjRespuestaValor {
  value: string;
  text?: string;
  /** Solo se completa cuando la pregunta es de tipo 'bool+text2'. */
  text2?: string;
  /** Metadata del archivo adjunto — solo para preguntas tipo 'file'. */
  file?: RespuestaArchivo;
}

export interface DdjjIntegrante {
  asociadoId: string;
  nombres?: string;
  apellSolt?: string;
  parentesco?: string;
  esTitular: boolean;
  preguntasAplicables: string[];
  respuestas: Record<string, DdjjRespuestaValor>;
  estado: 'en_curso' | 'completada';
}

export interface DdjjResponse {
  catalogo: DdjjPregunta[];
  integrantes: DdjjIntegrante[];
}

/**
 * Base de los endpoints de DDJJ. En modo `bis` (Fase 2 alta-directa) apunta a
 * `/portal-bis/:token/ddjj` (activación de un legajo_bis: solo los integrantes
 * nuevos). En el flujo del titular queda el path histórico.
 */
function ddjjBase(token: string, bis?: boolean): string {
  return bis ? `/portal-bis/${token}/ddjj` : `/portal/legajo/${token}/ddjj`;
}

export async function getDdjj(
  token: string,
  opts?: { bis?: boolean },
): Promise<DdjjResponse> {
  const { data } = await api.get<DdjjResponse>(ddjjBase(token, opts?.bis));
  return data;
}

export async function guardarDdjj(
  token: string,
  asociadoId: string,
  respuestas: Record<string, DdjjRespuestaValor>,
  completar = false,
  opts?: { bis?: boolean },
): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>(ddjjBase(token, opts?.bis), {
    asociadoId,
    respuestas,
    completar,
  });
  return data;
}

/**
 * Sube un archivo adjunto para una pregunta DDJJ tipo 'file' (ej. CUD).
 * Devuelve la metadata del archivo persistido; el blob queda en GridFS.
 */
export async function subirArchivoDdjj(
  token: string,
  asociadoId: string,
  codigoPregunta: string,
  file: File,
): Promise<RespuestaArchivo> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post<RespuestaArchivo>(
    `/portal/legajo/${token}/ddjj/${asociadoId}/archivo/${codigoPregunta}`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function eliminarArchivoDdjj(
  token: string,
  asociadoId: string,
  codigoPregunta: string,
): Promise<{ ok: boolean }> {
  const { data } = await api.delete<{ ok: boolean }>(
    `/portal/legajo/${token}/ddjj/${asociadoId}/archivo/${codigoPregunta}`,
  );
  return data;
}

export interface FinalizarLegajoResp {
  ok: boolean;
  legajoNumero?: string;
  estado?: string;
  /** True si el titular reportó campos como incorrectos durante el onboarding. */
  tieneObservacionesPendientes?: boolean;
  cantidadObservacionesPendientes?: number;
}

export async function finalizarLegajo(token: string): Promise<FinalizarLegajoResp> {
  const { data } = await api.post<FinalizarLegajoResp>(`/portal/legajo/${token}/finalizar`);
  return data;
}

// ---------- Plan superador ----------

export interface PlanComparable {
  codigo: 'MS1' | 'MS2';
  nombre: string;
  color: 'rojo' | 'verde';
}

export interface BeneficioComparable {
  texto: string;
  ms1: boolean;
  ms2: boolean;
}

export type TramoEtario = 'menor-45' | '45-59' | '60-mas';

export interface PrecioPorTramo {
  tramo: TramoEtario;
  label: string;
  /** Delta MS2 - base con IVA (lo que paga el socio). */
  precioConIva: number;
  /** MS2 completo con IVA (referencia). */
  ms2ConIva: number;
  /** Base con IVA (lo que cubre la empresa). */
  baseConIva: number;
}

export interface CondicionesPlanSuperador {
  permanenciaMinimaMeses: number;
  moraMaxDias: number;
  vigencia: string;
}

export interface PlanSuperadorCatalogo {
  planActual: PlanComparable;
  planSuperador: PlanComparable;
  beneficios: BeneficioComparable[];
  preciosPorTramo: PrecioPorTramo[];
  condiciones: CondicionesPlanSuperador;
}

export interface DesgloseIntegrantePrecio {
  nombre: string;
  edad: number;
  tramo: TramoEtario;
  /** Cuota MS2 completa sin IVA (referencia: lo que valdría el plan total). */
  ms2SinIva: number;
  /** Cuota MS1 base sin IVA (lo que paga la empresa). */
  basesinIva: number;
  /** Delta MS2 - base sin IVA (lo que paga el socio). */
  precioSinIva: number;
  /** Idem con IVA — el monto que se cobra. */
  precioConIva: number;
}

export interface PrecioGrupoCalculado {
  desglose: DesgloseIntegrantePrecio[];
  ms2TotalSinIva: number;
  baseTotalSinIva: number;
  subtotalSinIva: number;
  iva: number;
  totalConIva: number;
  vigencia: string;
}

export interface PlanSuperadorEstado {
  catalogo: PlanSuperadorCatalogo;
  precioGrupo: PrecioGrupoCalculado;
  elegido: boolean | null;
  decididoAt?: string;
}

export async function getPlanSuperador(
  token: string,
): Promise<PlanSuperadorEstado> {
  const { data } = await api.get<PlanSuperadorEstado>(
    `/portal/legajo/${token}/plan-superador`,
  );
  return data;
}

export interface AceptacionTycMs2 {
  aceptadoAt: string;
  versionDocumento: string;
  ip?: string;
  userAgent?: string;
}

export async function elegirPlanSuperador(
  token: string,
  aceptar: boolean,
  tycMs2?: { versionDocumento: string },
): Promise<{
  ok: boolean;
  elegido: boolean;
  decididoAt: string;
  aceptacionTycMs2: AceptacionTycMs2 | null;
}> {
  const body: { aceptar: boolean; tycMs2?: { versionDocumento: string } } = { aceptar };
  if (aceptar && tycMs2) body.tycMs2 = tycMs2;
  const { data } = await api.post<{
    ok: boolean;
    elegido: boolean;
    decididoAt: string;
    aceptacionTycMs2: AceptacionTycMs2 | null;
  }>(`/portal/legajo/${token}/plan-superador`, body);
  return data;
}

export interface InteresContactoMs2 {
  solicitadoAt: string;
  resuelto?: boolean;
}

/**
 * El titular eligió "Me interesa que me contacten" en el paso del plan
 * superador. El backend marca interesContactoMs2 + deja en MS1
 * (planSuperadorElegido=false). Idempotente.
 */
export async function marcarInteresContactoMs2(
  token: string,
): Promise<{ ok: boolean; elegido: boolean; interesContacto: InteresContactoMs2 }> {
  const { data } = await api.post<{
    ok: boolean;
    elegido: boolean;
    interesContacto: InteresContactoMs2;
  }>(`/portal/legajo/${token}/plan-superador/interes-contacto`, {});
  return data;
}

// ---------- Pago ----------

export type ProveedorPagoId = 'medicus_pay';
export type EstadoPago = 'pendiente' | 'aprobado' | 'rechazado' | 'cancelado';

export interface MetodoPago {
  proveedor: ProveedorPagoId;
  nombre: string;
  descripcion: string;
  habilitado: boolean;
  motivoDeshabilitado?: string;
}

export interface PagoActual {
  proveedor: ProveedorPagoId;
  estado: EstadoPago;
  monto: number;
  externalId?: string;
  referenciaExterna?: string;
  urlCheckout?: string;
  intentos: Array<{
    proveedor: ProveedorPagoId;
    estado: EstadoPago;
    monto: number;
    externalId?: string;
    motivoRechazo?: string;
    at: string;
  }>;
  iniciadoAt: string;
  actualizadoAt: string;
}

export interface MetodosPagoResp {
  monto: number;
  moneda: 'ARS';
  plan: string;
  metodos: MetodoPago[];
  pagoActual: PagoActual | null;
}

export async function getMetodosPago(token: string): Promise<MetodosPagoResp> {
  const { data } = await api.get<MetodosPagoResp>(`/portal/legajo/${token}/pago/metodos`);
  return data;
}

export interface IniciarPagoBody {
  proveedor?: ProveedorPagoId;
}

export type IniciarPagoResp =
  | {
      modo: 'directo';
      estado: 'aprobado' | 'rechazado' | 'cancelado';
      externalId?: string;
      motivoRechazo?: string;
      intentos: number;
      max: number;
    }
  | {
      modo: 'redirect';
      estado: 'pendiente';
      urlCheckout: string;
      referenciaExterna: string;
      intentos: number;
      max: number;
    };

export async function iniciarPago(
  token: string,
  body: IniciarPagoBody,
): Promise<IniciarPagoResp> {
  const { data } = await api.post<IniciarPagoResp>(
    `/portal/legajo/${token}/pago/iniciar`,
    body,
  );
  return data;
}

export async function confirmarPago(
  token: string,
  body: { externalId?: string; estado?: EstadoPago },
): Promise<{ estado: EstadoPago; externalId?: string; intentos: number; max: number }> {
  const { data } = await api.post<{
    estado: EstadoPago;
    externalId?: string;
    intentos: number;
    max: number;
  }>(`/portal/legajo/${token}/pago/confirmar`, body);
  return data;
}
